/**
 * Cookie Consent Banner — GDPR-compliant cookie consent management.
 *
 * Features:
 *   - Banner at bottom of page with Accept All / Essential Only / Cookie Settings
 *   - Settings modal with toggles for Essential (always on), Analytics, Preferences
 *   - Stores consent in `ts_cookie_consent` cookie as JSON
 *   - Checks consent on page load; hides banner if already consented
 *   - "Cookie settings" link in footer re-opens the modal
 *   - WCAG 2.2 AA accessible: focus trap, keyboard navigation, aria-labels
 *   - Works with both light and dark themes via CSS custom properties
 *
 * Cookie format:
 *   ts_cookie_consent = { essential: true, analytics: boolean, preferences: boolean }
 *
 * Usage:
 *   import { initCookieConsent } from './cookie-consent.js';
 *   initCookieConsent();
 */

// ── Types ───────────────────────────────────────────────────────────────

export interface CookieConsentPreferences {
  readonly essential: true; // Always true — cannot be disabled
  readonly analytics: boolean;
  readonly preferences: boolean;
  readonly marketing: boolean;
}

// ── Constants ───────────────────────────────────────────────────────────

const COOKIE_NAME = 'ts_cookie_consent';
const COOKIE_MAX_AGE_DAYS = 365;
// Stable random visitor identifier (NOT email/IP) used to correlate the
// server-side GDPR consent ledger rows across grant/update/withdraw. 32 random
// bytes → 64 hex chars, satisfying the API's visitorToken 32–64 length rule.
const VISITOR_TOKEN_COOKIE = 'ts_visitor_token';
// Server consent API (see cookieConsentRoutes.ts / cookieConsentSchemas.ts).
const CONSENT_API_URL = '/v1/cookie-consent';
const CONSENT_WITHDRAW_API_URL = '/v1/cookie-consent/withdraw';
// Consent policy version recorded alongside each event for accountability.
const CONSENT_POLICY_VERSION = '1.0';
const BANNER_ID = 'ts-cookie-consent-banner';
const MODAL_ID = 'ts-cookie-consent-modal';
const MODAL_OVERLAY_ID = 'ts-cookie-consent-overlay';

// ── Cookie helpers ──────────────────────────────────────────────────────

/**
 * Read the consent cookie and parse its JSON value.
 * Returns null if the cookie is missing or malformed.
 */
export function getConsentCookie(): CookieConsentPreferences | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.trim().split('=');
    if (name === COOKIE_NAME) {
      try {
        const value = decodeURIComponent(valueParts.join('='));
        const parsed: unknown = JSON.parse(value);
        if (
          parsed !== null &&
          typeof parsed === 'object' &&
          'essential' in parsed &&
          'analytics' in parsed &&
          'preferences' in parsed
        ) {
          const consent = parsed as CookieConsentPreferences;
          if (
            consent.essential === true &&
            typeof consent.analytics === 'boolean' &&
            typeof consent.preferences === 'boolean' &&
            (typeof consent.marketing === 'boolean' || consent.marketing === undefined)
          ) {
            // Backfill marketing if missing from old cookies
            if (typeof consent.marketing !== 'boolean') {
              return { ...consent, marketing: false };
            }
            return consent;
          }
        }
      } catch {
        // Malformed cookie — treat as no consent
      }
    }
  }
  return null;
}

/**
 * Write the consent cookie with the given preferences.
 * Sets SameSite=Lax for CSRF protection and a 1-year expiry.
 */
export function setConsentCookie(prefs: CookieConsentPreferences): void {
  const value = JSON.stringify(prefs);
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
}

// ── DOM creation helpers ────────────────────────────────────────────────

function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: Array<HTMLElement | string> = [],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, val] of Object.entries(attrs)) {
    el.setAttribute(key, val);
  }
  for (const child of children) {
    if (typeof child === 'string') {
      el.appendChild(document.createTextNode(child));
    } else {
      el.appendChild(child);
    }
  }
  return el;
}

// ── Self-contained styles (shared component; no host CSS dependency) ──────

const STYLE_ID = 'ts-cookie-consent-styles';

/** Inject the cookie banner + modal CSS once. CSP-safe (style-src 'unsafe-inline'). */
function ensureCookieConsentStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${BANNER_ID}.ts-cookie-banner{position:fixed;left:0;right:0;bottom:0;z-index:9999;background:#fff;color:#1a1a2e;border-top:1px solid #e2e8f0;box-shadow:0 -4px 20px rgba(0,0,0,.12);padding:1rem;font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif}
.ts-cookie-banner__inner{max-width:1100px;margin:0 auto;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:.75rem 1.5rem}
.ts-cookie-banner__text{margin:0;font-size:.875rem;line-height:1.5;color:#334155;flex:1 1 320px}
.ts-cookie-banner__actions{display:flex;flex-wrap:wrap;gap:.5rem;align-items:center}
.ts-cookie-btn{display:inline-flex;align-items:center;justify-content:center;min-height:var(--ts-touch-target-min,44px);padding:.75rem 1rem;border-radius:6px;font-size:.875rem;font-weight:600;cursor:pointer;border:1px solid transparent;font-family:inherit;line-height:1.2;white-space:nowrap;text-decoration:none;box-sizing:border-box}
.ts-cookie-btn--primary{background:#1B4D5C;color:#fff;border-color:#1B4D5C}
.ts-cookie-btn--primary:hover{background:#153F4C}
.ts-cookie-btn--secondary{background:#fff;color:#1B4D5C;border-color:#1B4D5C}
.ts-cookie-btn--secondary:hover{background:#f1f5f9}
.ts-cookie-btn--link{background:transparent;color:#1B4D5C;border-color:transparent;text-decoration:underline}
.ts-cookie-btn:focus-visible{outline:2px solid #1B4D5C;outline-offset:2px}
.ts-cookie-overlay{position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.5)}
.ts-cookie-modal{position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:10001;width:92%;max-width:520px;max-height:85vh;overflow:auto;background:#fff;color:#1a1a2e;border-radius:12px;box-shadow:0 10px 40px rgba(0,0,0,.25);font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif}
.ts-cookie-modal__header{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 1.5rem;border-bottom:1px solid #e2e8f0}
.ts-cookie-modal__title{margin:0;font-size:1.125rem;font-weight:700;color:#1a1a2e}
.ts-cookie-modal__close{background:transparent;border:0;font-size:1.5rem;line-height:1;cursor:pointer;color:#64748b;padding:.25rem .5rem;border-radius:6px;min-height:var(--ts-touch-target-min,44px);min-width:var(--ts-touch-target-min,44px);box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center}
.ts-cookie-modal__close:hover{background:#f1f5f9}
.ts-cookie-modal__body{padding:1.25rem 1.5rem;display:flex;flex-direction:column;gap:.25rem}
.ts-cookie-modal__footer{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:.5rem;padding:1rem 1.5rem;border-top:1px solid #e2e8f0}
.ts-cookie-toggle-row,.ts-cookie-toggle-group{display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;padding:.75rem 0;border-bottom:1px solid #f1f5f9}
.ts-cookie-toggle-label{font-weight:600;color:#1a1a2e;font-size:.9rem}
.ts-cookie-toggle-desc{font-size:.8rem;color:#64748b;margin-top:.2rem;line-height:1.4}
@media(max-width:640px){.ts-cookie-banner__inner{flex-direction:column;align-items:stretch}.ts-cookie-banner__actions{justify-content:stretch}.ts-cookie-banner__actions .ts-cookie-btn{flex:1}}`;
  document.head.appendChild(style);
}

// ── Banner ──────────────────────────────────────────────────────────────

function createBanner(): HTMLElement {
  const banner = createElement('div', {
    id: BANNER_ID,
    role: 'dialog',
    'aria-label': 'Cookie consent',
    'aria-modal': 'false',
    class: 'ts-cookie-banner',
  });

  const container = createElement('div', { class: 'ts-cookie-banner__inner' });

  const text = createElement('p', { class: 'ts-cookie-banner__text' }, [
    "We use essential cookies to make timeslot.ie work. We'd also like to use analytics cookies to understand how you use our site.",
  ]);

  const actions = createElement('div', { class: 'ts-cookie-banner__actions' });

  const acceptAllBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'ts-cookie-btn ts-cookie-btn--primary',
      'aria-label': 'Accept all cookies',
    },
    ['Accept all'],
  );

  const essentialOnlyBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'ts-cookie-btn ts-cookie-btn--secondary',
      // WCAG 2.5.3 (Label in Name): the accessible name must contain the visible text.
      'aria-label': 'Essential only — accept essential cookies only',
    },
    ['Essential only'],
  );

  const settingsBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'ts-cookie-btn ts-cookie-btn--link',
      'aria-label': 'Open cookie settings',
    },
    ['Cookie settings'],
  );

  acceptAllBtn.addEventListener('click', () => {
    saveConsent({ essential: true, analytics: true, preferences: true, marketing: true });
    hideBanner();
  });

  essentialOnlyBtn.addEventListener('click', () => {
    saveConsent({ essential: true, analytics: false, preferences: false, marketing: false });
    hideBanner();
  });

  settingsBtn.addEventListener('click', () => {
    openSettingsModal();
  });

  actions.appendChild(acceptAllBtn);
  actions.appendChild(essentialOnlyBtn);
  actions.appendChild(settingsBtn);

  container.appendChild(text);
  container.appendChild(actions);
  banner.appendChild(container);

  return banner;
}

// ── Settings Modal ─────────────────────────────────────────────────────

function createSettingsModal(): { overlay: HTMLElement; modal: HTMLElement } {
  const overlay = createElement('div', {
    id: MODAL_OVERLAY_ID,
    class: 'ts-cookie-overlay',
    'aria-hidden': 'true',
  });

  const modal = createElement('div', {
    id: MODAL_ID,
    role: 'dialog',
    'aria-label': 'Cookie preferences',
    'aria-modal': 'true',
    class: 'ts-cookie-modal',
    tabindex: '-1',
  });

  const header = createElement('div', { class: 'ts-cookie-modal__header' });
  const title = createElement('h2', { class: 'ts-cookie-modal__title' }, ['Cookie Preferences']);
  const closeBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'ts-cookie-modal__close',
      'aria-label': 'Close cookie preferences',
    },
    ['\u00D7'],
  );

  header.appendChild(title);
  header.appendChild(closeBtn);

  const body = createElement('div', { class: 'ts-cookie-modal__body' });

  // Essential toggle (always on, disabled)
  const essentialGroup = createToggleGroup(
    'essential',
    'Essential',
    'Required for the website to function. Cannot be disabled.',
    true,
    true,
  );

  // Analytics toggle
  const analyticsGroup = createToggleGroup(
    'analytics',
    'Analytics',
    'Help us understand how visitors use our site so we can improve it.',
    false,
    false,
  );

  // Preferences toggle
  const preferencesGroup = createToggleGroup(
    'preferences',
    'Preferences',
    'Remember your settings and personalisation choices.',
    false,
    false,
  );

  // Marketing toggle
  const marketingGroup = createToggleGroup(
    'marketing',
    'Marketing',
    'Allow us to send you relevant offers and promotions.',
    false,
    false,
  );

  body.appendChild(essentialGroup);
  body.appendChild(analyticsGroup);
  body.appendChild(preferencesGroup);
  body.appendChild(marketingGroup);

  const footer = createElement('div', { class: 'ts-cookie-modal__footer' });

  const saveBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'ts-cookie-btn ts-cookie-btn--primary',
      'aria-label': 'Save cookie preferences',
    },
    ['Save preferences'],
  );

  footer.appendChild(saveBtn);

  modal.appendChild(header);
  modal.appendChild(body);
  modal.appendChild(footer);

  // Event handlers
  closeBtn.addEventListener('click', () => closeSettingsModal());
  overlay.addEventListener('click', () => closeSettingsModal());

  saveBtn.addEventListener('click', () => {
    const analyticsInput = modal.querySelector<HTMLInputElement>('#ts-cookie-toggle-analytics');
    const preferencesInput = modal.querySelector<HTMLInputElement>('#ts-cookie-toggle-preferences');
    const marketingInput = modal.querySelector<HTMLInputElement>('#ts-cookie-toggle-marketing');
    saveConsent(
      {
        essential: true,
        analytics: analyticsInput?.checked ?? false,
        preferences: preferencesInput?.checked ?? false,
        marketing: marketingInput?.checked ?? false,
      },
      'settings_modal',
    );
    closeSettingsModal();
    hideBanner();
  });

  // Keyboard handling: Escape to close, Tab trap
  modal.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeSettingsModal();
      return;
    }

    if (e.key === 'Tab') {
      trapFocus(e, modal);
    }
  });

  return { overlay, modal };
}

function createToggleGroup(
  id: string,
  label: string,
  description: string,
  checked: boolean,
  disabled: boolean,
): HTMLElement {
  const group = createElement('div', { class: 'ts-cookie-toggle-group' });

  const row = createElement('div', { class: 'ts-cookie-toggle-row' });

  const labelEl = createElement(
    'label',
    {
      for: `ts-cookie-toggle-${id}`,
      class: 'ts-cookie-toggle-label',
    },
    [label],
  );

  const toggle = createElement('input', {
    type: 'checkbox',
    id: `ts-cookie-toggle-${id}`,
    class: 'ts-cookie-toggle',
    role: 'switch',
    'aria-label': `${label} cookies`,
    ...(checked ? { checked: '' } : {}),
    ...(disabled ? { disabled: '', 'aria-disabled': 'true' } : {}),
  }) as HTMLInputElement;

  if (checked) {
    toggle.checked = true;
  }
  if (disabled) {
    toggle.disabled = true;
  }

  row.appendChild(labelEl);
  row.appendChild(toggle);

  const desc = createElement('p', { class: 'ts-cookie-toggle-desc' }, [description]);

  group.appendChild(row);
  group.appendChild(desc);

  return group;
}

// ── Focus trap ─────────────────────────────────────────────────────────

function trapFocus(e: KeyboardEvent, container: HTMLElement): void {
  const focusableSelectors =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
    (el) => !el.hasAttribute('disabled'),
  );

  if (focusable.length === 0) return;

  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;

  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

// ── State management ────────────────────────────────────────────────────

let bannerEl: HTMLElement | null = null;
let modalEl: HTMLElement | null = null;
let overlayEl: HTMLElement | null = null;
let previousFocusEl: HTMLElement | null = null;

/** Where the consent decision originated (mirrors the server sourceSchema). */
type ConsentSource = 'banner' | 'settings_modal';

/**
 * Read an arbitrary first-party cookie by name. Returns null if absent.
 */
function readCookie(name: string): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [key, ...valueParts] = cookie.trim().split('=');
    if (key === name) {
      return decodeURIComponent(valueParts.join('='));
    }
  }
  return null;
}

/**
 * Return the visitor's stable consent token, generating and persisting a fresh
 * 64-hex-char random token (32 bytes) on first use. This is an opaque random
 * id — never an email or IP — so it cannot be enumerated back to a person.
 */
function getOrCreateVisitorToken(): string {
  const existing = readCookie(VISITOR_TOKEN_COOKIE);
  if (existing && /^[a-f0-9]{32,64}$/i.test(existing)) {
    return existing;
  }
  const bytes = new Uint8Array(32);
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  const token = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${VISITOR_TOKEN_COOKIE}=${token}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
  return token;
}

/**
 * Best-effort POST of the consent decision to the server-side GDPR ledger
 * (cookie_consent_events). Fire-and-forget: a network/API failure must never
 * block or throw in the consent UI. An "essential only" decision (analytics,
 * preferences and marketing all off) is recorded as a withdrawal; any other
 * decision is a grant (banner) or update (settings modal).
 */
function recordConsentServerSide(prefs: CookieConsentPreferences, source: ConsentSource): void {
  try {
    const visitorToken = getOrCreateVisitorToken();
    const essentialOnly = !prefs.analytics && !prefs.preferences && !prefs.marketing;

    if (essentialOnly) {
      void fetch(CONSENT_WITHDRAW_API_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({ visitorToken, source }),
      }).catch(() => {
        /* best-effort: consent cookie is already set client-side */
      });
      return;
    }

    void fetch(CONSENT_API_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({
        visitorToken,
        action: source === 'settings_modal' ? 'update' : 'grant',
        // The server categories schema tracks essential/analytics/preferences.
        categories: {
          essential: true,
          analytics: prefs.analytics,
          preferences: prefs.preferences,
        },
        source,
        policyVersion: CONSENT_POLICY_VERSION,
      }),
    }).catch(() => {
      /* best-effort: consent cookie is already set client-side */
    });
  } catch {
    /* never let consent logging break the UI */
  }
}

function saveConsent(prefs: CookieConsentPreferences, source: ConsentSource = 'banner'): void {
  setConsentCookie(prefs);
  // Persist the decision to the server-side GDPR consent ledger (best-effort).
  recordConsentServerSide(prefs, source);
  // Dispatch a custom event so other scripts can react to consent changes
  const event = new CustomEvent('ts:cookie-consent', { detail: prefs });
  document.dispatchEvent(event);
}

function hideBanner(): void {
  if (bannerEl) {
    if (bannerEl.contains(document.activeElement)) {
      (document.activeElement as HTMLElement)?.blur();
    }
    bannerEl.setAttribute('aria-hidden', 'true');
    bannerEl.style.display = 'none';
  }
}

function showBanner(): void {
  if (bannerEl) {
    bannerEl.setAttribute('aria-hidden', 'false');
    bannerEl.style.display = '';
  }
}

function openSettingsModal(): void {
  if (!modalEl || !overlayEl) return;

  // Load current preferences into toggles
  const current = getConsentCookie();
  const analyticsToggle = modalEl.querySelector<HTMLInputElement>('#ts-cookie-toggle-analytics');
  const preferencesToggle = modalEl.querySelector<HTMLInputElement>(
    '#ts-cookie-toggle-preferences',
  );
  const marketingToggle = modalEl.querySelector<HTMLInputElement>('#ts-cookie-toggle-marketing');
  if (analyticsToggle) analyticsToggle.checked = current?.analytics ?? false;
  if (preferencesToggle) preferencesToggle.checked = current?.preferences ?? false;
  if (marketingToggle) marketingToggle.checked = current?.marketing ?? false;

  previousFocusEl = document.activeElement as HTMLElement | null;
  overlayEl.setAttribute('aria-hidden', 'false');
  overlayEl.style.display = '';
  modalEl.setAttribute('aria-hidden', 'false');
  modalEl.style.display = '';
  modalEl.focus();
}

function closeSettingsModal(): void {
  if (overlayEl) {
    overlayEl.setAttribute('aria-hidden', 'true');
    overlayEl.style.display = 'none';
  }
  if (modalEl) {
    modalEl.setAttribute('aria-hidden', 'true');
    modalEl.style.display = 'none';
  }
  // Return focus to the element that triggered the modal
  if (previousFocusEl) {
    previousFocusEl.focus();
    previousFocusEl = null;
  }
}

// ── Footer link binding ────────────────────────────────────────────────

function bindFooterCookieSettingsLink(): void {
  // Look for a link with data-cookie-settings or class ts-cookie-settings-link in the footer
  const footerLinks = document.querySelectorAll<HTMLElement>(
    '[data-cookie-settings], .ts-cookie-settings-link',
  );
  for (const link of footerLinks) {
    link.addEventListener('click', (e: Event) => {
      e.preventDefault();
      openSettingsModal();
    });
    link.setAttribute('role', 'button');
    link.setAttribute('aria-label', 'Open cookie preferences');
  }
}

// ── Initialisation ──────────────────────────────────────────────────────

/**
 * Initialise the cookie consent system.
 *
 * Call this once after DOMContentLoaded. It will:
 *   1. Check for existing consent cookie
 *   2. If no consent, show the banner
 *   3. Set up the settings modal
 *   4. Bind footer "Cookie settings" links
 */
export function initCookieConsent(): void {
  if (document.getElementById(BANNER_ID)) return;

  // The banner/modal classes have no CSS in any page bundle (this is a shared
  // component used by the public site AND the booking pages). Inject its styles once
  // so it is never unstyled regardless of which CSS bundle the host page loads.
  ensureCookieConsentStyles();

  // Create banner and modal
  bannerEl = createBanner();
  const { overlay, modal } = createSettingsModal();
  overlayEl = overlay;
  modalEl = modal;

  // Add to DOM
  document.body.appendChild(bannerEl);
  document.body.appendChild(overlayEl);
  document.body.appendChild(modalEl);

  // Initially hide modal
  overlayEl.style.display = 'none';
  modalEl.style.display = 'none';

  // Check existing consent
  const existing = getConsentCookie();
  if (existing !== null) {
    // Already consented — hide banner
    hideBanner();
  } else {
    // No consent — show banner
    showBanner();
  }

  // Bind footer links
  bindFooterCookieSettingsLink();
}

// ── Exported for testing ──────────────────────────────────────────────

export { COOKIE_NAME, openSettingsModal, closeSettingsModal, hideBanner, showBanner };
